import React from 'react';
import { StatusStep as StatusStepType } from '@/hooks/useStatusModal';
import { cn } from '@/lib/utils';

interface StatusStepProps {
  step: StatusStepType;
  isLast: boolean;
}

export const StatusStep: React.FC<StatusStepProps> = ({ step, isLast }) => {
  const getStepIcon = () => {
    switch (step.status) {
      case 'completed':
        return (
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center animate-scale-in">
            <svg 
              className="w-4 h-4 text-primary-foreground" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M5 13l4 4L19 7" 
              />
            </svg>
          </div>
        );
      case 'active':
        return (
          <div className="relative w-8 h-8 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center">
            <div className="w-4 h-4 rounded-full bg-primary animate-pulse" />
            <div className="absolute inset-0 rounded-full border-2 border-primary animate-spin border-t-transparent" />
          </div>
        );
      case 'error':
        return (
          <div className="w-8 h-8 rounded-full bg-destructive/20 border-2 border-destructive flex items-center justify-center">
            <svg 
              className="w-4 h-4 text-destructive" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M6 18L18 6M6 6l12 12" 
              />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-full bg-muted border-2 border-muted-foreground/30 flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-muted-foreground/30" />
          </div>
        );
    }
  };

  const getConnectorLine = () => {
    if (isLast) return null;
    
    return (
      <div 
        className={cn(
          "h-0.5 flex-1 mx-3 transition-colors duration-500",
          step.status === 'completed' ? "bg-primary" : "bg-muted-foreground/30"
        )}
      />
    );
  };

  return (
    <div className="flex items-center justify-center flex-1">
      {/* Step container */}
      <div className="flex flex-col items-center space-y-2 min-w-0">
        {/* Step icon */}
        {getStepIcon()}
        
        {/* Step label */}
        <div className="text-center">
          <p className={cn(
            "text-sm font-medium transition-colors duration-300",
            step.status === 'active' ? "text-primary" :
            step.status === 'completed' ? "text-primary" :
            step.status === 'error' ? "text-destructive" :
            "text-muted-foreground"
          )}>
            {step.label}
          </p>
          
          {/* Step description - only show for active step */}
          {step.status === 'active' && step.description && (
            <p className="text-xs text-muted-foreground mt-1 animate-fade-in">
              {step.description}
            </p>
          )}
        </div>
      </div>
      
      {/* Connector line */}
      {getConnectorLine()}
    </div>
  );
};